import { Group, ScrollArea, Stack } from "@mantine/core"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import {
  type OperationNodeDto,
  useFindOperationTreeByProductId,
} from "~/gen/api"
import { HorizontalDivider } from "~/pages/Brand/Products/Details/CustomFieldGraph/Divider"
import { VerticalDivider } from "~/pages/Brand/Products/Details/TraceabilityGraph/Divider"
import { TraceabilityTreeBranch } from "~/pages/Brand/Products/Details/TraceabilityGraph/TraceabilityTreeBranch"

type Props = {
  product: LibraryResponses.ProductDetails
}

const NO_ACTIVITY_NAME = "no-activity"
type RenderedChild = {
  node: OperationNodeDto
  hiddenAncestors: OperationNodeDto[]
}

export const ProductTraceability = ({ product }: Props) => {
  const { data: operationTreeResponse } = useFindOperationTreeByProductId(
    product.uuid
  )

  const operationTree = operationTreeResponse?.data
  const mainNodes = operationTree?.nodes.filter((node) => node.isMain)[0]

  if (!mainNodes) {
    return null
  }

  const map = new Map(operationTree?.nodes.map((node) => [node.id, node]))

  const result: OperationNodeDto[] = []
  const visited = new Set()

  function visit(item: OperationNodeDto) {
    if (!item || visited.has(item.id)) return

    visited.add(item.id)
    result.push(item)

    item.childrenRef.forEach((childId) => {
      visit(map.get(childId) as OperationNodeDto)
    })
  }

  const mainItem = operationTree?.nodes.find((node) => node.isMain)
  if (!mainItem) {
    return null
  }
  visit(mainItem)

  return (
    <ScrollArea w="100%" type="scroll" offsetScrollbars>
      {RenderTree(mainItem, map, false, [])}
    </ScrollArea>
  )
}

const RenderTree = (
  item: OperationNodeDto,
  map: Map<string, OperationNodeDto>,
  isFirstChild = true,
  ancestorReferences: string[]
) => {
  if (!item) return null

  const renderedChildren = getRenderedChildren(item, map)
  const shouldRenderCurrentNode = !isNodeWithoutActivity(item)
  const nextAncestorReferences = [...ancestorReferences, item.reference]

  if (!shouldRenderCurrentNode) {
    return (
      <Stack align="flex-start" justify="left">
        {renderedChildren.map(
          ({ node: childNode, hiddenAncestors }, childIndex) => (
            <Group key={childNode.id} gap={0} align="flex-start" wrap="nowrap">
              {childIndex > 0 && <HorizontalDivider />}
              {RenderTree(childNode, map, childIndex === 0, [
                ...nextAncestorReferences,
                ...hiddenAncestors.map((ancestor) => ancestor.reference),
              ])}
            </Group>
          )
        )}
      </Stack>
    )
  }

  return (
    <Group
      gap={0}
      align="flex-start"
      style={{ position: "relative" }}
      wrap="nowrap"
    >
      {!isFirstChild && <HorizontalDivider />}
      <TraceabilityTreeBranch
        node={item}
        hideFirstDivider={!isFirstChild}
        parentReferences={ancestorReferences}
      />
      {renderedChildren.length > 0 && (
        <Stack align="flex-start" justify="left">
          <VerticalDivider
            itemCount={getVerticalDividerCoveredCardsCount(
              renderedChildren,
              map
            )}
          />
          {renderedChildren.map(({ node: childNode, hiddenAncestors }) => (
            <Group key={childNode.id} gap={0} align="flex-start" wrap="nowrap">
              {!childNode.id.includes(item.type) && <HorizontalDivider />}
              {RenderTree(childNode, map, !childNode.id.includes(item.type), [
                ...nextAncestorReferences,
                ...hiddenAncestors.map((ancestor) => ancestor.reference),
              ])}
            </Group>
          ))}
        </Stack>
      )}
    </Group>
  )
}

const getVerticalDividerCoveredCardsCount = (
  children: RenderedChild[],
  map: Map<string, OperationNodeDto>
): number => {
  if (children.length <= 1) {
    return children.length
  }

  const siblingBranchRowsCount = children.map(({ node: childNode }) =>
    getRenderedRowsCount(childNode, map)
  )

  const coveredRowsBeforeLastSibling = siblingBranchRowsCount
    .slice(0, -1)
    .reduce((sum, rowsCount) => sum + rowsCount, 0)

  return coveredRowsBeforeLastSibling + 1
}

const getRenderedRowsCount = (
  item: OperationNodeDto,
  map: Map<string, OperationNodeDto>
): number => {
  const renderedChildren = getRenderedChildren(item, map)

  if (renderedChildren.length === 0) {
    return 1
  }

  return renderedChildren.reduce((sum, { node: childNode }) => {
    return sum + getRenderedRowsCount(childNode, map)
  }, 0)
}

const isNodeWithoutActivity = (node: OperationNodeDto): boolean => {
  return (
    node.operation === "not-entered-yet" &&
    node.activityName === NO_ACTIVITY_NAME
  )
}

const getRenderedChildren = (
  node: OperationNodeDto,
  map: Map<string, OperationNodeDto>,
  hiddenAncestors: OperationNodeDto[] = []
): RenderedChild[] => {
  return node.childrenRef.flatMap((childId) => {
    const childNode = map.get(childId)
    if (!childNode) {
      return []
    }

    if (isNodeWithoutActivity(childNode)) {
      return getRenderedChildren(childNode, map, [
        ...hiddenAncestors,
        childNode,
      ])
    }

    return [{ node: childNode, hiddenAncestors }]
  })
}
